import * as DialogPrimitive from "@radix-ui/react-dialog"
import { Link } from "@tanstack/react-router"
import { Search, X } from "lucide-react"
import * as React from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import type { NavGroupConfig, NavPage, NavTab } from "@/config/navigation"
import { cn } from "@/utils/styles.helpers"
import { LiveCountBadge } from "./live-count-badge"
import type { NavLocation } from "./nav-model"
import { pageHref, pageLiveBadge } from "./nav-model"

interface AllPagesMenuProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	groups: NavGroupConfig[]
	location?: NavLocation
}

interface PageRow {
	page: NavPage
	tabs: NavTab[]
}

function Highlight({ text, query }: { text: string; query: string }) {
	const index = query ? text.toLowerCase().indexOf(query) : -1
	if (index < 0) return <>{text}</>

	return (
		<>
			{text.slice(0, index)}
			<mark className="rounded-sm bg-primary/15 text-primary">
				{text.slice(index, index + query.length)}
			</mark>
			{text.slice(index + query.length)}
		</>
	)
}

function PageLiveBadge({ page }: { page: NavPage }) {
	const liveBadge = pageLiveBadge(page)
	return liveBadge ? <LiveCountBadge kind={liveBadge} /> : null
}

/**
 * The rows of one group column for a filter: a page shows when its name, its
 * group's name or one of its tabs matches; under it, the matching tabs (all
 * of them when there is no filter), leaving out the tab named like the page.
 */
function pageRows(group: NavGroupConfig, query: string): PageRow[] {
	const hit = (text: string) => !query || text.toLowerCase().includes(query)
	const rows: PageRow[] = []

	for (const page of group.pages) {
		const ownTabs =
			page.tabs.length > 1
				? page.tabs.filter((tab) => tab.title !== page.title)
				: []
		const tabHits = ownTabs.filter((tab) => hit(tab.title))

		if (hit(page.title) || hit(group.label) || tabHits.length > 0) {
			rows.push({ page, tabs: query ? tabHits : ownTabs })
		}
	}

	return rows
}

/** Full overlay over the content listing every visible page and tab, with a filter. */
export function AllPagesMenu({
	open,
	onOpenChange,
	groups,
	location,
}: AllPagesMenuProps) {
	const [filter, setFilter] = React.useState("")
	const inputRef = React.useRef<HTMLInputElement>(null)
	const query = filter.trim().toLowerCase()

	const columns = groups
		.map((group) => ({ group, rows: pageRows(group, query) }))
		.filter((column) => column.rows.length > 0)

	const close = () => onOpenChange(false)

	return (
		<DialogPrimitive.Root
			open={open}
			onOpenChange={(next) => {
				if (next) setFilter("")
				onOpenChange(next)
			}}
		>
			<DialogPrimitive.Portal>
				<DialogPrimitive.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-background md:left-[72px]" />
				<DialogPrimitive.Content
					aria-describedby={undefined}
					onOpenAutoFocus={(event) => {
						event.preventDefault()
						setFilter("")
						inputRef.current?.focus()
					}}
					className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 flex flex-col gap-5 overflow-y-auto px-6 py-5 outline-hidden md:left-[72px]"
				>
					<DialogPrimitive.Title className="sr-only">
						All pages
					</DialogPrimitive.Title>

					<div className="flex items-center gap-2">
						<label className="flex h-10 flex-1 items-center gap-2 rounded-md border bg-background px-3 shadow-xs focus-within:ring-2 focus-within:ring-ring">
							<Search className="size-4 shrink-0 text-muted-foreground" />
							<input
								ref={inputRef}
								value={filter}
								onChange={(event) => setFilter(event.target.value)}
								placeholder="Filter pages and tabs"
								aria-label="Filter pages and tabs"
								className="h-full w-full bg-transparent text-sm outline-hidden placeholder:text-muted-foreground"
							/>
						</label>
						<DialogPrimitive.Close asChild>
							<Button variant="outline" className="cursor-pointer">
								<X className="size-4" />
								Close
							</Button>
						</DialogPrimitive.Close>
					</div>

					{columns.length === 0 ? (
						<p className="text-sm text-muted-foreground">
							No page or tab matches "{filter.trim()}".
						</p>
					) : (
						<div className="columns-[12rem] gap-x-8">
							{columns.map(({ group, rows }) => (
								<section
									key={group.key}
									className="mb-5 min-w-0 break-inside-avoid"
								>
									<h2 className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
										<group.icon className="size-3.5" />
										<Highlight text={group.label} query={query} />
									</h2>
									<ul className="space-y-0.5">
										{rows.map(({ page, tabs }) => (
											<li key={page.title}>
												<Link
													to={pageHref(page)}
													onClick={close}
													className={cn(
														"flex items-center gap-2 rounded-md px-1.5 py-[3px] text-sm hover:bg-accent hover:text-accent-foreground",
														location?.page.title === page.title &&
															location.group.key === group.key &&
															"font-semibold text-primary",
													)}
												>
													<page.icon className="size-4 shrink-0 text-muted-foreground" />
													<span className="truncate">
														<Highlight text={page.title} query={query} />
													</span>
													{page.badge && (
														<Badge
															variant="outline"
															className="ml-auto h-5 px-1.5 text-xs"
														>
															{page.badge}
														</Badge>
													)}
													<PageLiveBadge page={page} />
												</Link>
												{tabs.length > 0 && (
													<ul className="ml-[1.375rem] border-l pl-2">
														{tabs.map((tab) => (
															<li key={tab.href}>
																<Link
																	to={tab.href}
																	onClick={close}
																	className={cn(
																		"flex items-center gap-2 rounded-md px-1.5 py-px text-[13px] text-muted-foreground hover:bg-accent hover:text-accent-foreground",
																		location?.tab.href === tab.href &&
																			"font-medium text-primary",
																	)}
																>
																	<span className="truncate">
																		<Highlight text={tab.title} query={query} />
																	</span>
																	{tab.liveBadge && (
																		<LiveCountBadge kind={tab.liveBadge} />
																	)}
																</Link>
															</li>
														))}
													</ul>
												)}
											</li>
										))}
									</ul>
								</section>
							))}
						</div>
					)}
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	)
}
